import { jwtDecode } from "jwt-decode";
import { create } from "zustand";
import { createJSONStorage, devtools, persist } from "zustand/middleware";

import { refreshRetryDelay, refreshSessionOnce } from "@/configs/request/refresh";
import type { Auth, User, UserRole, UserTokenPayload } from "@/types";

/**
 * Token'ni IMZOLANGAN qiymatdan qayta hisoblaydi.
 *
 * Ilgari `decodedToken` localStorage'ga o'zi yozilar va reload'da o'sha
 * yozuvga ishonilardi — ya'ni `localStorage["auth"]` ni qo'lda tahrirlab
 * `{"decodedToken":{"role":"admin"}}` qo'yish butun admin panelni ochardi.
 * Endi rol faqat serverning imzosi bilan kelgan token'dan o'qiladi.
 */
export const decodeAccessToken = (token?: string | null): UserTokenPayload | null => {
  if (!token) return null;
  try {
    const payload = jwtDecode<UserTokenPayload>(token);
    return payload && typeof payload.exp === "number" && payload.role ? payload : null;
  } catch {
    return null;
  }
};

/** Token bor va muddati o'tmagan bo'lsagina sessiya haqiqiy hisoblanadi. */
export const isTokenLive = (payload: UserTokenPayload | null | undefined): boolean =>
  !!payload && payload.exp * 1000 > Date.now();

/**
 * localStorage'ga tushadigan qism.
 *
 * Na access token, na refresh token. Faqat "sessiya ochiq edi" bayrog'i va
 * foydalanuvchi kartochkasi — reload paytida ekranni bo'sh ko'rsatmaslik
 * uchun.
 */
interface PersistedAuth {
  auth: boolean;
  data: Pick<Auth, "user"> | null;
}

interface AuthStoreProps {
  auth: boolean;
  data: Auth | null;
  decodedToken: UserTokenPayload | null;
  /**
   * Reload'dan keyin access token refresh token orqali qayta olinmaguncha
   * true. Guard'lar shu paytda login'ga uloqtirmasdan kutadi.
   */
  rehydrating: boolean;

  setAuth: (data: Auth) => void;
  updateTokens: (data: Partial<Auth>) => void;
  setUser: (user: User) => void;
  logout: () => void;
  /** Refresh token'dan yangi juftlik oladi. Muvaffaqiyatsiz bo'lsa — chiqish. */
  refreshSession: () => Promise<boolean>;
}

const sleep = (ms: number) => new Promise<void>(resolve => setTimeout(resolve, ms));

export const useAuthStore = create<AuthStoreProps>()(
  devtools(
    persist(
      (set, get) => ({
        auth: false,
        data: null,
        decodedToken: null,
        rehydrating: false,

        setAuth: data => {
          set({
            auth: true,
            data,
            decodedToken: decodeAccessToken(data.accessToken),
            rehydrating: false,
          });
        },

        updateTokens: data => {
          const current = get().data;

          if (!current) return;

          const newData = { ...current, ...data };

          set({
            auth: true,
            data: newData,
            decodedToken: decodeAccessToken(newData.accessToken),
            rehydrating: false,
          });
        },

        setUser: user => {
          const current = get().data;
          if (!current) return;
          set({ data: { ...current, user } });

          // Rolning ikkita manbasi bor edi: guard'lar token'dan, ekranlar
          // `data.user` dan o'qir edi. Student o'qituvchiga ko'tarilganda
          // sidebar o'qituvchi menyusini ochib, `/teacher/*` esa orqaga
          // qaytarardi. Token — yagona haqiqat manbai, shuning uchun rol
          // ajralib qolsa uni majburan yangilaymiz.
          const tokenRole = get().decodedToken?.role;
          if (tokenRole && user.role !== tokenRole) void get().refreshSession();
        },

        logout: () => {
          set({
            auth: false,
            data: null,
            decodedToken: null,
            rehydrating: false,
          });
        },

        refreshSession: async () => {
          // Refresh token HttpOnly cookie'da — uni JavaScript ko'rmaydi,
          // ya'ni "bormi?" deb oldindan tekshirib bo'lmaydi. Backend 401
          // qaytarsa sessiya yo'q degani.
          for (let attempt = 0; ; attempt += 1) {
            try {
              // Interceptor bilan BIR XIL va'da: reload'da do'kon va 401 ni
              // ushlagan so'rov ikki alohida yangilash yubormaydi.
              get().setAuth(await refreshSessionOnce());
              return true;
            } catch (error) {
              // Faqat server "sessiya yo'q" desa (401/403) chiqamiz. Tarmoq,
              // 502 yoki 429 — vaqtinchalik: kutib, qayta uriniladi
              // (`refreshRetryDelay`), ish yo'qolmaydi.
              const delay = refreshRetryDelay(attempt, error);
              if (delay === null) {
                get().logout();
                return false;
              }
              await sleep(delay);
              // Kutish paytida boshqa tabda chiqilgan bo'lishi mumkin.
              if (!get().auth) return false;
            }
          }
        },
      }),
      {
        name: "auth",
        storage: createJSONStorage(() => localStorage),

        /**
         * localStorage'da faqat foydalanuvchi kartochkasi qoladi.
         *
         * Ilgari bu yerda refresh token ham turardi va bitta XSS 30 kunlik
         * hisob ma'lumotini olib chiqa olardi — u sessiyani emas, HISOBNI
         * egallash uchun yetarli edi. Endi token `lu_rt` HttpOnly
         * cookie'sida va JavaScript uni umuman ko'rmaydi.
         *
         * Access token ham saqlanmaydi (xotirada qoladi), `decodedToken`
         * esa har safar imzolangan token'dan qayta hisoblanadi.
         */
        partialize: (state): PersistedAuth => ({
          auth: state.auth,
          data: state.data ? { user: state.data.user } : null,
        }),

        merge: (persisted, current) => {
          const saved = persisted as PersistedAuth | undefined;
          const wasSignedIn = !!saved?.auth && !!saved.data?.user;

          return {
            ...current,
            auth: wasSignedIn,
            // Tokenlar saqlanmaydi: access token'ni refresh olib keladi,
            // refresh token esa cookie'da va bu yerga umuman tushmaydi.
            data: saved?.data ? { ...saved.data, accessToken: "", refreshToken: "" } : null,
            // Saqlangan qiymatga ishonilmaydi — token kelgach hisoblanadi.
            decodedToken: null,
            rehydrating: wasSignedIn,
          };
        },

        onRehydrateStorage: () => state => {
          if (!state) return;
          // Access token saqlanmagan — uni cookie orqali qaytadan olamiz.
          // Cookie o'lgan bo'lsa `refreshSession` o'zi `logout` qiladi.
          if (state.auth) void state.refreshSession();
        },
      },
    ),
    { name: "Auth Store" },
  ),
);

/**
 * Amaldagi rol. Yagona manba — imzolangan access token, `data.user` emas:
 * `/auth/me` foydalanuvchini yangilaydi, lekin token eskiligicha qolishi
 * mumkin va ikkovi ajralib ketsa guard bilan ekran bir-biriga zid ishlaydi.
 */
export const useRole = (): UserRole | undefined => useAuthStore(state => state.decodedToken?.role);

/** Guard'lar uchun: token bor, muddati o'tmagan va sessiya ochiq. */
export const useIsAuthenticated = (): boolean =>
  useAuthStore(state => state.auth && isTokenLive(state.decodedToken));
